import React from "react";
import { NavLink, Outlet, Navigate, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, FileText, Video, Tags, Inbox, Settings, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/articles", label: "Articles", icon: FileText },
  { to: "/admin/videos", label: "Videos", icon: Video },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
];

export function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();

  if (!ready) return <div className="flex min-h-screen items-center justify-center bg-cream-200 font-sans text-steel-500">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="flex min-h-screen bg-cream-200">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-ink/10 bg-cream-100 lg:flex">
        <div className="border-b border-ink/10 px-6 py-5"><Logo showTagline={false} /></div>
        <nav className="flex-1 space-y-1 px-3 py-5">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-sm px-3 py-2.5 font-sans text-sm transition-colors ${
                  isActive ? "bg-ink text-cream-100" : "text-ink/70 hover:bg-cream-200"
                }`
              }
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-ink/10 px-3 py-4">
          <Link to="/" target="_blank" className="flex items-center gap-3 rounded-sm px-3 py-2.5 font-sans text-sm text-ink/70 hover:bg-cream-200">
            <ExternalLink className="h-4 w-4" /> View site
          </Link>
          <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-sm px-3 py-2.5 font-sans text-sm text-ink/70 hover:bg-cream-200" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64">
        {/* Mobile top bar */}
        <div className="flex items-center justify-between border-b border-ink/10 bg-cream-100 px-4 py-3 lg:hidden">
          <Logo showTagline={false} />
          <button onClick={doLogout} className="text-ink/70"><LogOut className="h-5 w-5" /></button>
        </div>
        <div className="flex gap-1 overflow-x-auto border-b border-ink/10 bg-cream-100 px-2 py-2 lg:hidden">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `whitespace-nowrap rounded-sm px-3 py-1.5 font-sans text-xs ${isActive ? "bg-ink text-cream-100" : "text-ink/70"}`}>{n.label}</NavLink>
          ))}
        </div>
        <main className="p-5 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
